import { useQuery } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import { LoadingPanel, ErrorNotice, EmptyState } from './ui/States';
import { fmt, dateShort, DASH } from '../lib/format';

interface QuizRow {
  cmid: number;
  activityName: string | null;
  respondents: number;
  averagePct: number | null;
  lastGradedAt: string | null;
}

const headCell = 'font-semibold text-small text-ink-2 py-[10px]';
const cell = 'py-[10px] text-table text-right tabular';

export default function MoodleQuizzes() {
  const { data, isLoading, error } = useQuery<{ quizzes: QuizRow[] }>(
    'moodle-quizzes',
    async () => (await api.get('/analytics/moodle/quizzes')).data.data,
  );
  return (
    <Panel className="mt-6">
      <PanelHeader title="Recording quiz results" note="Quizzes inside the session recordings, from Moodle" />
      {isLoading ? (
        <LoadingPanel label="Loading quiz results…" height={140} />
      ) : error || !data ? (
        <PanelBody>
          <ErrorNotice>Could not load quiz results.</ErrorNotice>
        </PanelBody>
      ) : data.quizzes.length === 0 ? (
        <EmptyState title="No quiz scores yet">
          Scores arrive with the nightly Moodle sync once the Moodle plugin release that exports them is live.
        </EmptyState>
      ) : (
        <PanelBody>
          <table className="w-full">
            <thead>
              <tr className="border-b border-line">
                <th className={`${headCell} text-left`}>Recording</th>
                <th className={`${headCell} text-right`}>Members scored</th>
                <th className={`${headCell} text-right`}>Average score</th>
                <th className={`${headCell} text-right`}>Latest</th>
              </tr>
            </thead>
            <tbody>
              {data.quizzes.map((q) => (
                <tr key={q.cmid} className="border-b border-line last:border-b-0">
                  <td className="py-[10px] text-table">{q.activityName ?? DASH}</td>
                  <td className={cell}>{fmt(q.respondents)}</td>
                  <td className={cell}>{q.averagePct === null ? DASH : `${q.averagePct}%`}</td>
                  <td className={`${cell} text-ink-2`}>{q.lastGradedAt ? dateShort(q.lastGradedAt) : DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PanelBody>
      )}
    </Panel>
  );
}
